/**
 * GridLevelValidator & Sanitizer
 *
 * Phase 1 of Map Pipeline Hardening.
 * Validates GridLevel JSON input and auto-corrects common mistakes
 * (wrong coordinate space, invalid tile types, missing fields).
 */

import { GRID_CONSTANTS } from '../constants/GridConstants';
import {
  GridLevel,
  LayerType,
  TileType,
  encodeTileKey,
} from '../TileEditorTypes';
import { gameLogger } from '../../utils/GameLogger';
import { validateReachGate } from './ReachabilityAnalyzer';
import { validateScenePropTemplates } from '../scene/townPropTemplates';
import { validateMountedSceneProps } from './mountedScenePropValidation';

// ==========================================
// Validation
// ==========================================

export interface GridValidationError {
  field: string;
  message: string;
  severity: 'error' | 'warning' | 'auto-fixed';
}

const REQUIRED_LAYERS: LayerType[] = [
  LayerType.GROUND,
  LayerType.COLLISION,
  LayerType.OBJECTS,
  LayerType.TRIGGERS,
  LayerType.ENTITIES,
];

const VALID_TILE_TYPES = new Set(
  Object.values(TileType).filter(v => typeof v === 'number') as number[]
);

const TILE_KEY_REGEX = /^-?\d+,-?\d+$/;

/**
 * Validate a GridLevel structure.
 * Returns an array of errors/warnings. Empty array = valid.
 */
export function validateGridLevel(level: any): GridValidationError[] {
  const errors: GridValidationError[] = [];

  // Required top-level fields
  if (!level) {
    errors.push({ field: 'root', message: 'Level data is null or undefined', severity: 'error' });
    return errors;
  }

  if (!level.id || typeof level.id !== 'string') {
    errors.push({ field: 'id', message: 'Missing or invalid level ID (must be string)', severity: 'error' });
  }
  errors.push(...validateScenePropTemplates(level.scene?.natureProps));
  errors.push(...validateMountedSceneProps(level));

  if (!level.name || typeof level.name !== 'string') {
    errors.push({ field: 'name', message: 'Missing or invalid level name', severity: 'warning' });
  }

  if (typeof level.widthTiles !== 'number' || level.widthTiles <= 0) {
    errors.push({ field: 'widthTiles', message: `Invalid widthTiles: ${level.widthTiles}`, severity: 'error' });
  }

  if (typeof level.heightTiles !== 'number' || level.heightTiles <= 0) {
    errors.push({ field: 'heightTiles', message: `Invalid heightTiles: ${level.heightTiles}`, severity: 'error' });
  }

  // Tile size
  if (level.tileSize && level.tileSize !== GRID_CONSTANTS.TILE_SIZE) {
    errors.push({
      field: 'tileSize',
      message: `tileSize is ${level.tileSize}, expected ${GRID_CONSTANTS.TILE_SIZE}`,
      severity: 'warning'
    });
  }

  // Layers
  if (!Array.isArray(level.layers)) {
    errors.push({ field: 'layers', message: 'Missing layers array', severity: 'error' });
  } else {
    const layerNames = level.layers.map((l: any) => l.name);
    for (const req of REQUIRED_LAYERS) {
      if (!layerNames.includes(req)) {
        errors.push({ field: 'layers', message: `Missing required layer: ${req}`, severity: 'warning' });
      }
    }

    // Validate tile data
    for (const layer of level.layers) {
      if (layer.tiles) {
        const tileEntries: [string, number][] = layer.tiles instanceof Map
          ? Array.from((layer.tiles as Map<string, number>).entries())
          : Object.entries(layer.tiles).map(([k, v]) => [k, v as number] as [string, number]);

        for (const [key, value] of tileEntries) {
          if (!TILE_KEY_REGEX.test(key as string)) {
            errors.push({
              field: `layers.${layer.name}.tiles`,
              message: `Invalid tile key: "${key}" (expected "x,y" integers)`,
              severity: 'error'
            });
          }
          if (!VALID_TILE_TYPES.has(value as number)) {
            errors.push({
              field: `layers.${layer.name}.tiles`,
              message: `Invalid tile type ${value} at key "${key}" (valid: 0-${Math.max(...VALID_TILE_TYPES)})`,
              severity: 'warning'
            });
          }
        }

        // Detect pixel coordinates (values > 500 strongly suggest pixels, not tiles)
        for (const [key] of tileEntries) {
          const { x, y } = typeof key === 'string'
            ? { x: parseInt(key.split(',')[0]), y: parseInt(key.split(',')[1]) }
            : { x: 0, y: 0 };
          if (Math.abs(x) > 500 || Math.abs(y) > 500) {
            errors.push({
              field: `layers.${layer.name}.tiles`,
              message: `Tile at "${key}" has suspiciously large coordinates — likely pixel coords instead of tile coords`,
              severity: 'warning'
            });
            break; // One warning is enough
          }
        }
      }
    }
  }

  // Spawn points
  if (level.spawnPoints) {
    for (let i = 0; i < level.spawnPoints.length; i++) {
      const sp = level.spawnPoints[i];
      if (sp.x > 1000 || sp.y > 1000) {
        errors.push({
          field: `spawnPoints[${i}]`,
          message: `Spawn (${sp.x}, ${sp.y}) looks like pixel coords — should be tile coords`,
          severity: 'warning'
        });
      }
    }
  }

  // Reachability gate validation — check reach.gate on platforms (PlatformerLevel format)
  // and on entity platforms (GridLevel entities layer).
  // Unknown gate values → warning (per MAP_REACHABILITY_TOOLKIT.md §3.2).
  if (Array.isArray(level.platforms)) {
    for (let i = 0; i < level.platforms.length; i++) {
      const platform = level.platforms[i];
      if (!platform?.reach) continue;
      const gateErr = validateReachGate(platform);
      if (gateErr) {
        errors.push({
          field: `platforms[${i}].reach.gate`,
          message: gateErr,
          severity: 'warning',
        });
      }
    }
  }

  // Also check GridLevel entities layer for reach gates on platform entities
  if (Array.isArray(level.layers)) {
    for (const layer of level.layers) {
      if (layer.name === LayerType.ENTITIES && Array.isArray(layer.entities)) {
        for (let i = 0; i < layer.entities.length; i++) {
          const entity = layer.entities[i];
          if (!entity?.reach) continue;
          const gateErr = validateReachGate(entity);
          if (gateErr) {
            errors.push({
              field: `entities[${i}].reach.gate`,
              message: gateErr,
              severity: 'warning',
            });
          }
        }
      }
    }
  }

  return errors;
}

// ==========================================
// Sanitization (Auto-Correction)
// ==========================================

/**
 * Auto-correct common mistakes in a GridLevel.
 * Returns a clean GridLevel + list of corrections made.
 *
 * Handles:
 * - Missing layers → adds defaults
 * - Pixel coordinates → divides by tileSize
 * - Invalid tile types → clamps to valid range
 * - Missing tileSize → sets to GRID_CONSTANTS.TILE_SIZE
 * - Object tile data → converts to Map
 * - Missing spawnPoints → generates default
 */
export function sanitizeGridLevel(input: any): { level: GridLevel; fixes: string[] } {
  const fixes: string[] = [];
  const tileSize = GRID_CONSTANTS.TILE_SIZE;

  // Clone to avoid mutating input
  const level = JSON.parse(JSON.stringify(input));

  // --- ID & Name ---
  if (!level.id) {
    level.id = `map_${Date.now()}`;
    fixes.push(`Generated missing ID: ${level.id}`);
  }
  if (!level.name) {
    level.name = level.id;
    fixes.push(`Set name to ID: ${level.name}`);
  }

  // --- Version ---
  if (!level.version) {
    level.version = GRID_CONSTANTS.DEFAULT_VERSION;
    fixes.push(`Set default version: ${GRID_CONSTANTS.DEFAULT_VERSION}`);
  }

  // --- Tile Size ---
  if (level.tileSize !== tileSize) {
    fixes.push(`Corrected tileSize from ${level.tileSize} to ${tileSize}`);
    level.tileSize = tileSize;
  }

  // --- Dimensions ---
  if (!level.widthTiles || level.widthTiles <= 0) {
    level.widthTiles = GRID_CONSTANTS.DEFAULT_WIDTH;
    fixes.push(`Set default widthTiles: ${GRID_CONSTANTS.DEFAULT_WIDTH}`);
  }
  if (!level.heightTiles || level.heightTiles <= 0) {
    level.heightTiles = GRID_CONSTANTS.DEFAULT_HEIGHT;
    fixes.push(`Set default heightTiles: ${GRID_CONSTANTS.DEFAULT_HEIGHT}`);
  }

  // --- Detect pixel coordinates globally ---
  let pixelCoordsDetected = false;
  if (level.layers && Array.isArray(level.layers)) {
    for (const layer of level.layers) {
      const tiles = layer.tiles;
      if (!tiles) continue;
      const entries = tiles instanceof Map ? Array.from(tiles.keys()) : Object.keys(tiles);
      for (const key of entries) {
        const parts = (key as string).split(',');
        if (parts.length === 2) {
          const x = parseInt(parts[0]);
          const y = parseInt(parts[1]);
          if (Math.abs(x) > 500 || Math.abs(y) > 500) {
            pixelCoordsDetected = true;
            break;
          }
        }
      }
      if (pixelCoordsDetected) break;
    }
  }

  // --- Layers ---
  if (!Array.isArray(level.layers)) {
    level.layers = [];
    fixes.push('Created empty layers array');
  }

  // Ensure all required layers exist
  const existingLayerNames = new Set(level.layers.map((l: any) => l.name));
  for (const req of REQUIRED_LAYERS) {
    if (!existingLayerNames.has(req)) {
      level.layers.push({
        name: req,
        visible: true,
        locked: false,
        opacity: 1.0,
        tiles: {},
        ...(req === LayerType.ENTITIES ? { entities: [] } : {}),
      });
      fixes.push(`Added missing layer: ${req}`);
    }
  }

  // Process each layer's tiles
  for (const layer of level.layers) {
    if (!layer.tiles) {
      layer.tiles = {};
      continue;
    }

    // Convert Map to plain object if needed (for JSON round-tripping)
    let tileObj: Record<string, number>;
    if (layer.tiles instanceof Map) {
      tileObj = Object.fromEntries(layer.tiles);
    } else {
      tileObj = layer.tiles;
    }

    const cleanTiles: Record<string, number> = {};

    for (const [key, value] of Object.entries(tileObj)) {
      let tileValue = value as number;

      // Validate key format
      if (!TILE_KEY_REGEX.test(key)) continue;

      let { x, y } = { x: parseInt(key.split(',')[0]), y: parseInt(key.split(',')[1]) };

      // Auto-correct pixel coordinates
      if (pixelCoordsDetected) {
        x = Math.round(x / tileSize);
        y = Math.round(y / tileSize);
      }

      // Clamp tile type to valid range
      if (!VALID_TILE_TYPES.has(tileValue)) {
        tileValue = TileType.FLOOR; // Default to floor for unrecognized types
      }

      // Skip empty tiles
      if (tileValue === TileType.EMPTY) continue;

      cleanTiles[encodeTileKey(x, y)] = tileValue;
    }

    layer.tiles = cleanTiles;
  }

  if (pixelCoordsDetected) {
    fixes.push('Detected pixel coordinates in tiles — divided all tile keys by tileSize');
  }

  // --- Spawn Points ---
  if (!level.spawnPoints || level.spawnPoints.length === 0) {
    level.spawnPoints = [{ x: 5, y: 5 }];
    fixes.push('Added default spawn point at (5, 5)');
  } else if (pixelCoordsDetected) {
    level.spawnPoints = level.spawnPoints.map((sp: any) => ({
      x: Math.round(sp.x / tileSize),
      y: Math.round(sp.y / tileSize),
    }));
    fixes.push('Converted spawn point pixel coordinates to tile coordinates');
  }

  // --- Gravity ---
  if (!level.gravity) {
    level.gravity = GRID_CONSTANTS.DEFAULT_GRAVITY;
    fixes.push(`Set default gravity: ${GRID_CONSTANTS.DEFAULT_GRAVITY}`);
  }

  // --- Background Color ---
  if (!level.backgroundColor) {
    level.backgroundColor = GRID_CONSTANTS.DEFAULT_BG_COLOR;
    fixes.push(`Set default backgroundColor: ${GRID_CONSTANTS.DEFAULT_BG_COLOR}`);
  }

  // Log
  if (fixes.length > 0) {
    gameLogger.info('level', `[sanitizeGridLevel] Applied ${fixes.length} fixes to "${level.id}":`);
    fixes.forEach(f => gameLogger.info('level', `  → ${f}`));
  }

  return { level: level as GridLevel, fixes };
}

/**
 * Convenience: validate + sanitize in one call.
 * Returns a guaranteed-clean GridLevel.
 */
export function validateAndSanitize(input: any): { level: GridLevel; errors: GridValidationError[]; fixes: string[] } {
  const errors = validateGridLevel(input);
  const { level, fixes } = sanitizeGridLevel(input);
  return { level, errors, fixes };
}
